"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, CircleAlert as AlertCircle, Camera, CircleCheck as CheckCircle2, ChevronRight, Gauge, PhoneCall, Radio, Search, ShieldCheck, Thermometer, Wrench, Zap } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Link } from "@/components/common/Link";
interface DiagnosticTool {
  id: string;
  name: string;
  category: string;
  accuracy: string;
  description: string;
  readinessTime: string;
  bestFor: string;
  badge: string;
}
interface Capability {
  id: string;
  title: string;
  spec: string;
  description: string;
  responseWindow: string;
  standard: string;
}
const diagnosticTools: DiagnosticTool[] = [{
  id: "cam-hd",
  name: "Fiber-Optic Sewer Inspection System",
  category: "Visual Inspection",
  accuracy: "1080p Real-Time",
  description: "High-definition micro cameras with digital self-leveling heads to locate blockages, root intrusions, and pipe fractures up to 200 feet deep.",
  readinessTime: "Under 5 mins",
  bestFor: "Sewer collapses, lateral line breaks, deep clogs",
  badge: "Non-Invasive"
}, {
  id: "acoustic-sensor",
  name: "Acoustic Leak Detection Arrays",
  category: "Sub-Surface Audio",
  accuracy: "+/- 6-inch pinpointing",
  description: "Frequency-filtered ground microphones and correlators that isolate escaping water sounds beneath concrete slabs and finished soil.",
  readinessTime: "Immediate",
  bestFor: "Hidden slab leaks, pressurized pipe bursts",
  badge: "Zero Digging"
}, {
  id: "thermal-imaging",
  name: "Infrared Thermal Moisture Mapping",
  category: "Thermal Analysis",
  accuracy: "0.05°C sensitivity",
  description: "Calibrated radiometric thermal imagers that trace hidden hot and cold moisture trails behind drywall, subfloors, and ceiling voids.",
  readinessTime: "Immediate",
  bestFor: "Concealed wall leaks, hot line fractures",
  badge: "Wall-Safe"
}, {
  id: "hydrostatic-test",
  name: "Digital Hydrostatic Pressure Loggers",
  category: "Pressure Diagnostics",
  accuracy: "0.1 PSI resolution",
  description: "Continuous pressure logging to verify whole-home structural supply integrity and immediately isolate hidden micro-fissures in water mains.",
  readinessTime: "Under 10 mins",
  bestFor: "Gradual pressure drops, municipal main line anomalies",
  badge: "Precision"
}];
const repairCapabilities: Capability[] = [{
  id: "hydro-jetting",
  title: "4,000 PSI Hydro-Jetting & Scouring",
  spec: "Up to 4,000 PSI @ 12 GPM",
  description: "Heavy-duty rotating jet heads scour calcified scale, industrial grease, and invasive tree roots to restore full pipe bore diameter.",
  responseWindow: "On-Site Ready",
  standard: "Commercial & Residential"
}, {
  id: "slab-bypass",
  title: "Trenchless Pipe Relining & Slab Bypass",
  spec: "PEX-A / Epoxy CIPP Liners",
  description: "Direct rerouting of fractured slab plumbing with structural non-invasive epoxy sleeves or durable ceiling-run PEX lines.",
  responseWindow: "Same-Day Deployment",
  standard: "ASTM F1216 Compliant"
}, {
  id: "water-heater-rebuild",
  title: "Emergency Water Heater Rebuild & Replacement",
  spec: "Gas, Electric & Hybrid Tankless",
  description: "Mobile stock of OEM pressure relief valves, heating elements, thermostats, and complete unit swaps up to 100-gallon capacity.",
  responseWindow: "Immediate Dispatch",
  standard: "ASME Safety Code"
}, {
  id: "main-line-clamp",
  title: "Main Water Line Spot Clamping & Sleeving",
  spec: "Full-Pressure Compression Fittings",
  description: "Rapid hydraulic containment of sheared yard lines and municipal meter connections without extended neighborhood outages.",
  responseWindow: "24/7 Mobile Van",
  standard: "AWWA Verified"
}];
export default function Diagnostics() {
  const shouldReduceMotion = useReducedMotion();
  const [selectedTool, setSelectedTool] = useState<string>("cam-hd");
  const activeToolData = diagnosticTools.find(tool => tool.id === selectedTool) || diagnosticTools[0];
  return <section id="technicaldiagnosticscapabilities" data-nav="light" className="relative bg-background py-16 md:py-24 overflow-hidden border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="outline" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary border-primary/20 bg-primary/5">
              <Activity className="size-3.5 text-primary mr-1" />
              Technical Capabilities
            </Badge>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Jonesboro Emergency Fleet
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Advanced Diagnostic Equipment & Heavy-Duty Emergency Repair
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            We do not guess. Our Jonesboro service trucks arrive equipped with industrial-grade acoustic, optical, and thermal instruments to locate and resolve deep plumbing emergencies quickly with zero unnecessary demolition.
          </p>
        </div>

        {/* Tabbed Capabilities System */}
        <Tabs defaultValue="diagnostics" className="w-full">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-6 border-b border-border mb-8">
            <TabsList className="bg-muted p-1 h-auto rounded-lg">
              <TabsTrigger value="diagnostics" className="px-4 py-2 text-sm font-medium data-[state=active]:bg-card data-[state=active]:text-card-foreground data-[state=active]:shadow-sm">
                <Search className="size-4 mr-2" />
                Precision Diagnostics
              </TabsTrigger>
              <TabsTrigger value="repairs" className="px-4 py-2 text-sm font-medium data-[state=active]:bg-card data-[state=active]:text-card-foreground data-[state=active]:shadow-sm">
                <Wrench className="size-4 mr-2" />
                Heavy-Duty Repair Fleet
              </TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/60 px-3 py-1.5 rounded-md border border-border">
              <ShieldCheck className="size-4 text-primary" />
              <span>Calibrated instruments on every 24/7 service vehicle</span>
            </div>
          </div>

          {/* TAB 1: DIAGNOSTICS */}
          <TabsContent value="diagnostics" className="focus-visible:outline-none">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              {/* Tool Selection List */}
              <div className="lg:col-span-5 flex flex-col gap-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                  Select Diagnostic Instrument
                </p>

                {diagnosticTools.map((tool, i) => {
                const isSelected = selectedTool === tool.id;
                return <div key={tool.id} data-index={i} onClick={() => setSelectedTool(tool.id)} className={`cursor-pointer text-left p-4 rounded-xl border transition-all ${isSelected ? "bg-card border-primary ring-1 ring-primary shadow-sm" : "bg-background hover:bg-card/60 border-border"}`}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-sm font-semibold text-foreground">
                          {tool.name}
                        </span>
                        <Badge variant={isSelected ? "default" : "secondary"} className="text-[11px]">
                          {tool.badge}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2">
                        <span className="flex items-center gap-1">
                          <Gauge className="size-3.5 text-primary" />
                          {tool.accuracy}
                        </span>
                        <span className="flex items-center gap-1">
                          <Zap className="size-3.5 text-primary" />
                          {tool.readinessTime}
                        </span>
                      </div>
                    </div>;
              })}
              </div>

              {/* Diagnostic Visualizer Card */}
              <div className="lg:col-span-7">
                <Card className="border border-border bg-card shadow-md overflow-hidden relative">
                  <div className="p-6 sm:p-8">
                    <div className="flex flex-wrap items-center justify-between gap-3 pb-6 border-b border-border">
                      <div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                          {activeToolData.category}
                        </span>
                        <h3 className="text-xl sm:text-2xl font-bold text-card-foreground mt-1">
                          {activeToolData.name}
                        </h3>
                      </div>
                      <Badge variant="default" className="bg-primary text-primary-foreground font-mono">
                        {activeToolData.accuracy}
                      </Badge>
                    </div>

                    <div className="py-6 space-y-4">
                      <p className="text-card-foreground text-sm sm:text-base leading-relaxed">
                        {activeToolData.description}
                      </p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                        <div className="p-4 rounded-lg bg-background border border-border">
                          <span className="text-xs text-muted-foreground font-medium block">
                            Deployment Readiness
                          </span>
                          <span className="text-sm font-semibold text-foreground mt-0.5 block">
                            {activeToolData.readinessTime}
                          </span>
                        </div>
                        <div className="p-4 rounded-lg bg-background border border-border">
                          <span className="text-xs text-muted-foreground font-medium block">
                            Best Suited Emergency
                          </span>
                          <span className="text-sm font-semibold text-foreground mt-0.5 block">
                            {activeToolData.bestFor}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Diagnostic Sensor Readout Mock */}
                    <div className="rounded-lg bg-background border border-border p-4 mt-2">
                      <div className="flex items-center justify-between mb-3 text-xs">
                        <span className="font-mono text-muted-foreground flex items-center gap-2">
                          <span className="size-2 rounded-full bg-chart-4 animate-pulse" />
                          LIVE CALIBRATION BENCHMARK
                        </span>
                        <span className="font-mono text-muted-foreground">JONESBORO FLEET #4</span>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-card p-2 rounded border border-border">
                          <span className="text-[10px] text-muted-foreground block uppercase">Depth Rating</span>
                          <span className="text-xs font-bold text-foreground">Up to 200 FT</span>
                        </div>
                        <div className="bg-card p-2 rounded border border-border">
                          <span className="text-[10px] text-muted-foreground block uppercase">Accuracy Margin</span>
                          <span className="text-xs font-bold text-chart-4">&lt; 0.5%</span>
                        </div>
                        <div className="bg-card p-2 rounded border border-border">
                          <span className="text-[10px] text-muted-foreground block uppercase">Safety Clearance</span>
                          <span className="text-xs font-bold text-foreground">100% Non-Destructive</span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 pt-4 border-t border-border">
                      <div className="text-xs text-muted-foreground">
                        Require an urgent diagnostic scan in Jonesboro?
                      </div>
                      <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground font-medium text-xs px-4 py-2 hover:bg-primary/90 transition-colors">
                        <PhoneCall className="size-3.5" />
                        Call Emergency Tech
                      </a>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* TAB 2: REPAIRS */}
          <TabsContent value="repairs" className="focus-visible:outline-none">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {repairCapabilities.map((cap, i) => <div key={cap.id} data-index={i}>
                  <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <Badge variant="outline" className="text-xs text-primary border-primary/20 bg-primary/5">
                          {cap.standard}
                        </Badge>
                        <span className="text-xs font-mono text-muted-foreground">
                          {cap.responseWindow}
                        </span>
                      </div>
                      <CardTitle className="text-lg font-bold text-card-foreground">
                        {cap.title}
                      </CardTitle>
                      <CardDescription className="text-xs text-primary font-medium">
                        Specification: {cap.spec}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-card-foreground/90 leading-relaxed">
                        {cap.description}
                      </p>

                      <div className="p-3 bg-background rounded-lg border border-border flex items-center justify-between text-xs">
                        <span className="text-muted-foreground font-medium flex items-center gap-1.5">
                          <CheckCircle2 className="size-4 text-chart-4" />
                          Direct Master Plumber Execution
                        </span>
                        <Badge variant="secondary" className="text-[10px]">
                          24/7 Verified
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                </div>)}
            </div>
          </TabsContent>
        </Tabs>

        {/* Technical Specification Bar */}
        <div className="mt-12 rounded-xl bg-secondary p-6 md:p-8 border border-border">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
            <div className="md:col-span-1 border-b md:border-b-0 md:border-r border-border pb-4 md:pb-0 md:pr-4">
              <span className="text-xs uppercase tracking-wider text-secondary-foreground/70 font-semibold block">
                Quality Protocol
              </span>
              <h4 className="text-lg font-bold text-secondary-foreground mt-1">
                Zero Guesswork Guarantee
              </h4>
            </div>

            <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-md bg-background border border-border text-primary shrink-0">
                  <Camera className="size-4" />
                </div>
                <div>
                  <h5 className="text-xs font-bold text-secondary-foreground">Digital Footage Provided</h5>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Clear video and diagnostic logs shared with homeowners before repair work starts.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 rounded-md bg-background border border-border text-primary shrink-0">
                  <Thermometer className="size-4" />
                </div>
                <div>
                  <h5 className="text-xs font-bold text-secondary-foreground">Thermal Verification</h5>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Post-repair scans ensure hidden moisture and pressure leaks are completely stopped.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 rounded-md bg-background border border-border text-primary shrink-0">
                  <AlertCircle className="size-4" />
                </div>
                <div>
                  <h5 className="text-xs font-bold text-secondary-foreground">Jonesboro Local Fleet</h5>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Rapid staging across Clayton County with trucks carrying complete diagnostic toolsets.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA Row */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div>
            <h4 className="text-base font-semibold text-foreground">
              Facing an active leak or sewer backup right now?
            </h4>
            <p className="text-sm text-muted-foreground">
              Our technicians dispatch with fully loaded diagnostic vans ready for immediate action.
            </p>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <Link to="/contact/" className="w-full sm:w-auto">
              <Button variant="outline" className="w-full sm:w-auto text-xs font-semibold">
                Request Diagnostic Visit
              </Button>
            </Link>
            <a href="tel:4708479076" className="w-full sm:w-auto">
              <Button variant="default" className="w-full sm:w-auto text-xs font-semibold gap-1.5">
                <PhoneCall className="size-3.5" />
                (470) 847-9076
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>;
}